import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  AlertTriangle,
  Brain,
  Building2,
  Check,
  CheckCircle,
  Copy,
  Edit2,
  Globe,
  History,
  Inbox,
  Info,
  Loader2,
  MessageSquare,
  MessagesSquare,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
  XCircle,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { useGlobalToast } from "@/contexts/ToastContext";
import { useConfirm } from "@/contexts/ConfirmContext";
import ConversationComposer from "@/components/ConversationComposer";
import SimpleMarkdown from "@/components/SimpleMarkdown";

/**
 * SUPERADMIN → IA → "Habla con Carmen" (sep-2026).
 *
 * Chat libre con el cerebro de Carmen (como se habla con Claude): lee el
 * prompt real de un negocio, sus llamadas/chats recientes y los casos
 * guardados de "Probar a Carmen", y PROPONE cambios (acciones) que el
 * superadmin aplica con un clic. Nada se aplica solo.
 *
 * Contrato de backend en `/admin/carmen-brain/*` (`{ success, data }` /
 * `{ success:false, code, message }`, 503 + code "CARMEN_BRAIN_DISABLED" =
 * apagado → aviso, no error). El SERVIDOR guarda cada turno y reconstruye el
 * historial: el cliente manda `{ conversationId, locationId, message }` (ya
 * NO el historial entero). Las conversaciones se listan/renombran/borran vía
 * `/admin/carmen-brain/conversations*` y se persiste la última abierta en
 * `localStorage` para reabrirla al recargar.
 *
 * El listado de negocios reutiliza `GET /admin/carmen-sim/businesses` (ya
 * usado por `CarmenSim.tsx`).
 */

type ActionKind = "addendum_negocio" | "orden_global";
type ActionStatus = "pending" | "applied" | "discarded";
type MainTab = "chat" | "historico" | "conversaciones";

const LAST_CONVERSATION_KEY = "rm_carmen_brain_last_conversation";

interface BusinessItem {
  locationId: string;
  locationName: string;
  organizationName: string;
}

interface ChatToolCall {
  name: string;
  resumen: string;
}

interface BrainAction {
  id: string;
  kind: ActionKind;
  title: string;
  rationale: string;
  before: string | null;
  after: string;
  locationId: string | null;
  locationName: string | null;
  caseId: string | null;
  status: ActionStatus;
  createdAt: string;
  appliedAt: string | null;
  discardReason: string | null;
}

/** Respuesta de aplicar/descartar: la Acción + de qué conversación viene (si
 *  procede de una), para poder refrescar esa conversación y que aparezca la
 *  nota de sistema. */
interface ActionMutationResult extends BrainAction {
  conversationId?: string | null;
}

interface ChatResponseData {
  conversationId: string;
  reply: string;
  toolCalls: ChatToolCall[];
  actions: BrainAction[];
  usage: { costEur: number };
}

interface ChatTurn {
  role: "user" | "assistant" | "system";
  text: string;
  toolCalls?: ChatToolCall[];
  actions?: BrainAction[];
  time: string;
  variant?: "error" | "info";
}

interface ConversationSummary {
  id: string;
  title: string;
  locationId: string | null;
  locationName: string | null;
  messagesCount: number;
  lastMessageAt: string;
  createdAt: string;
}

interface ConversationMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  toolCalls: ChatToolCall[];
  actions: BrainAction[];
  createdAt: string;
}

/** Tipo mínimo de la cabecera de conversación en `GET conversations/:id`
 *  (el resto del payload del backend se ignora, mismo criterio que en otros
 *  fetches puntuales de este fichero). */
interface ConversationHeader {
  id: string;
  title: string;
  locationId: string | null;
}

function nowLabel(): string {
  return new Date().toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function timeLabel(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function formatDateTime(iso: string): string {
  return format(new Date(iso), "d MMM, HH:mm", { locale: es });
}

function errorCode(err: unknown): string | undefined {
  return (err as { response?: { data?: { code?: string } } })?.response?.data
    ?.code;
}

function toChatTurn(m: ConversationMessage): ChatTurn {
  return {
    role: m.role,
    text: m.content,
    toolCalls: m.toolCalls,
    actions: m.actions,
    time: timeLabel(m.createdAt),
  };
}

// localStorage puede no estar disponible (modo privado, etc.) — nunca debe
// romper la pantalla, simplemente no se recuerda la última conversación.
function readLastConversationId(): string | null {
  try {
    return localStorage.getItem(LAST_CONVERSATION_KEY);
  } catch {
    return null;
  }
}
function writeLastConversationId(id: string | null): void {
  try {
    if (id) localStorage.setItem(LAST_CONVERSATION_KEY, id);
    else localStorage.removeItem(LAST_CONVERSATION_KEY);
  } catch {
    // sin memoria, sin drama
  }
}

function mainTabClasses(active: boolean): string {
  return `inline-flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
    active
      ? "bg-primary-100 text-primary-700"
      : "text-gray-600 hover:bg-gray-100"
  }`;
}

// ---------------------------------------------------------------------------
// Tarjeta de acción propuesta: reutilizada en el chat, el panel lateral Y el
// histórico a ancho completo
// ---------------------------------------------------------------------------

function ActionKindChip({ kind }: { kind: ActionKind }) {
  return kind === "orden_global" ? (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
      <Globe className="w-3 h-3" /> Orden para el cerebro global
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
      <Building2 className="w-3 h-3" /> Añadido del negocio
    </span>
  );
}

function ActionStatusChip({ status }: { status: ActionStatus }) {
  if (status === "applied") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
        <CheckCircle className="w-3 h-3" /> Aplicada
      </span>
    );
  }
  if (status === "discarded") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
        <XCircle className="w-3 h-3" /> Descartada
      </span>
    );
  }
  return null;
}

function ActionCard({
  action,
  busy,
  highlighted,
  onApply,
  onMarkSent,
  onCopyOrder,
  onDiscard,
}: {
  action: BrainAction;
  busy: boolean;
  highlighted: boolean;
  onApply: (action: BrainAction) => void;
  onMarkSent: (action: BrainAction) => void;
  onCopyOrder: (action: BrainAction) => void;
  onDiscard: (action: BrainAction, reason: string) => void;
}) {
  const [discardOpen, setDiscardOpen] = useState(false);
  const [reason, setReason] = useState("");
  const isPending = action.status === "pending";
  const isGlobal = action.kind === "orden_global";

  return (
    <div
      id={`action-${action.id}`}
      className={`rounded-xl border p-3 text-sm space-y-2 ${
        isGlobal
          ? "border-purple-200 bg-purple-50/50"
          : "border-blue-200 bg-blue-50/50"
      } ${highlighted ? "ring-2 ring-primary-400" : ""}`}
    >
      <div className="flex items-center gap-1.5 flex-wrap">
        <ActionKindChip kind={action.kind} />
        <ActionStatusChip status={action.status} />
      </div>
      <p className="font-semibold text-gray-900">{action.title}</p>
      <p className="text-xs text-gray-600">{action.rationale}</p>
      {action.locationName && (
        <p className="text-xs text-gray-500">Negocio: {action.locationName}</p>
      )}

      <div className="space-y-1.5">
        <div>
          <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-0.5">
            Antes
          </p>
          <pre className="whitespace-pre-wrap break-words bg-white border border-gray-200 rounded p-2 font-mono text-[11px] text-gray-600 max-h-40 overflow-y-auto">
            {action.before ?? "— (sin instrucciones previas)"}
          </pre>
        </div>
        <div>
          <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-0.5">
            Después
          </p>
          <pre className="whitespace-pre-wrap break-words bg-gray-900 text-gray-100 rounded p-2 font-mono text-[11px] max-h-40 overflow-y-auto">
            {action.after}
          </pre>
        </div>
      </div>

      {!isPending && (
        <p className="text-xs text-gray-500">
          {action.status === "applied" ? "Aplicada" : "Descartada"}
          {action.appliedAt
            ? ` el ${formatDateTime(action.appliedAt)}`
            : ` · propuesta el ${formatDateTime(action.createdAt)}`}
        </p>
      )}
      {action.status === "discarded" && action.discardReason && (
        <p className="text-xs text-gray-500 italic">
          Motivo: {action.discardReason}
        </p>
      )}

      {isPending && (
        <div className="pt-1">
          {discardOpen ? (
            <div className="space-y-1.5">
              <input
                type="text"
                autoFocus
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="¿Por qué se descarta?"
                maxLength={300}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setDiscardOpen(false);
                    setReason("");
                  }}
                  disabled={busy}
                  className="flex-1 px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-600 text-xs hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => onDiscard(action, reason.trim())}
                  disabled={busy || !reason.trim()}
                  className="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-lg bg-gray-700 text-white text-xs font-medium hover:bg-gray-800 disabled:opacity-50"
                >
                  {busy ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    "Confirmar descarte"
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {isGlobal ? (
                <>
                  <button
                    type="button"
                    onClick={() => onCopyOrder(action)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-purple-300 text-purple-700 text-xs font-medium hover:bg-purple-100"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copiar orden para Claude
                  </button>
                  <button
                    type="button"
                    onClick={() => onMarkSent(action)}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-purple-600 text-white text-xs font-medium hover:bg-purple-700 disabled:opacity-50"
                  >
                    {busy ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <CheckCircle className="w-3.5 h-3.5" />
                    )}
                    Marcar como enviada
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => onApply(action)}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 disabled:opacity-50"
                >
                  {busy ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <CheckCircle className="w-3.5 h-3.5" />
                  )}
                  Aplicar
                </button>
              )}
              <button
                type="button"
                onClick={() => setDiscardOpen(true)}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-300 text-gray-600 text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                <XCircle className="w-3.5 h-3.5" /> Descartar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Burbuja de chat (con tool calls discretos + tarjetas de acción). Los turnos
// de sistema ("Acción «…» APLICADA…") NO pasan por aquí: se pintan como una
// línea centrada discreta directamente en la lista de turnos.
// ---------------------------------------------------------------------------

function BrainChatBubble({
  turn,
  busyActionId,
  highlightActionId,
  onApply,
  onMarkSent,
  onCopyOrder,
  onDiscard,
}: {
  turn: ChatTurn;
  busyActionId: string | null;
  highlightActionId: string | null;
  onApply: (action: BrainAction) => void;
  onMarkSent: (action: BrainAction) => void;
  onCopyOrder: (action: BrainAction) => void;
  onDiscard: (action: BrainAction, reason: string) => void;
}) {
  const isUser = turn.role === "user";
  const bubbleClass = isUser
    ? "bg-primary-600 text-white rounded-tr-md"
    : turn.variant === "error"
      ? "bg-red-50 text-red-700 border border-red-100 rounded-tl-md"
      : turn.variant === "info"
        ? "bg-blue-50 text-blue-700 border border-blue-100 rounded-tl-md"
        : "bg-white border border-gray-200 text-gray-800 rounded-tl-md";

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[92%] md:max-w-[75%] min-w-0 space-y-1.5">
        {!isUser && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-gray-500">Carmen</span>
            <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold">
              IA
            </span>
          </div>
        )}
        <div
          className={`px-3.5 py-2.5 rounded-2xl text-sm break-words ${bubbleClass}`}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap">{turn.text}</p>
          ) : turn.variant === "error" ? (
            <p className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {turn.text}
            </p>
          ) : turn.variant === "info" ? (
            <p className="flex items-start gap-2">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {turn.text}
            </p>
          ) : (
            <SimpleMarkdown text={turn.text} />
          )}
        </div>
        <p
          className={`text-[11px] text-gray-400 ${isUser ? "text-right" : ""}`}
        >
          {turn.time}
        </p>

        {!isUser && turn.toolCalls && turn.toolCalls.length > 0 && (
          <ul className="space-y-1">
            {turn.toolCalls.map((tc, i) => (
              <li
                key={`${tc.name}-${i}`}
                className="flex items-start gap-1.5 text-xs text-gray-500"
              >
                <Search className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-gray-400" />
                <span>{tc.resumen}</span>
              </li>
            ))}
          </ul>
        )}

        {!isUser && turn.actions && turn.actions.length > 0 && (
          <div className="space-y-2">
            {turn.actions.map((a) => (
              <ActionCard
                key={a.id}
                action={a}
                busy={busyActionId === a.id}
                highlighted={highlightActionId === a.id}
                onApply={onApply}
                onMarkSent={onMarkSent}
                onCopyOrder={onCopyOrder}
                onDiscard={onDiscard}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel lateral: SOLO pendientes (el histórico vive ahora en su propia
// pestaña a ancho completo)
// ---------------------------------------------------------------------------

function PendingActionsPanel({
  actions,
  loading,
  error,
  onReload,
  busyActionId,
  highlightActionId,
  onApply,
  onMarkSent,
  onCopyOrder,
  onDiscard,
}: {
  actions: BrainAction[] | null;
  loading: boolean;
  error: string | null;
  onReload: () => void;
  busyActionId: string | null;
  highlightActionId: string | null;
  onApply: (action: BrainAction) => void;
  onMarkSent: (action: BrainAction) => void;
  onCopyOrder: (action: BrainAction) => void;
  onDiscard: (action: BrainAction, reason: string) => void;
}) {
  const pending = useMemo(
    () =>
      (actions ?? [])
        .filter((a) => a.status === "pending")
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        ),
    [actions],
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-shrink-0 flex items-center gap-1 p-2 border-b border-gray-100">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-gray-700">
          <Inbox className="w-3.5 h-3.5" /> Pendientes
          {pending.length > 0 ? ` (${pending.length})` : ""}
        </span>
        <button
          onClick={onReload}
          disabled={loading}
          title="Actualizar"
          className="ml-auto p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {loading && !actions ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
          </div>
        ) : error ? (
          <p className="text-xs text-red-600 text-center py-6">{error}</p>
        ) : pending.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-8">
            Sin acciones pendientes.
          </p>
        ) : (
          pending.map((a) => (
            <ActionCard
              key={a.id}
              action={a}
              busy={busyActionId === a.id}
              highlighted={highlightActionId === a.id}
              onApply={onApply}
              onMarkSent={onMarkSent}
              onCopyOrder={onCopyOrder}
              onDiscard={onDiscard}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pestaña "Histórico": ancho completo, tarjetas completas + filtros +
// búsqueda
// ---------------------------------------------------------------------------

function HistoryTab({
  actions,
  loading,
  error,
  onReload,
  businesses,
  busyActionId,
  highlightActionId,
  onApply,
  onMarkSent,
  onCopyOrder,
  onDiscard,
}: {
  actions: BrainAction[] | null;
  loading: boolean;
  error: string | null;
  onReload: () => void;
  businesses: BusinessItem[];
  busyActionId: string | null;
  highlightActionId: string | null;
  onApply: (action: BrainAction) => void;
  onMarkSent: (action: BrainAction) => void;
  onCopyOrder: (action: BrainAction) => void;
  onDiscard: (action: BrainAction, reason: string) => void;
}) {
  const [statusFilter, setStatusFilter] = useState<
    "all" | "applied" | "discarded"
  >("all");
  const [locationFilter, setLocationFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (actions ?? [])
      .filter((a) => a.status !== "pending")
      .filter((a) => statusFilter === "all" || a.status === statusFilter)
      .filter(
        (a) => locationFilter === "all" || a.locationId === locationFilter,
      )
      .filter(
        (a) =>
          !term ||
          `${a.title} ${a.rationale} ${a.locationName ?? ""}`
            .toLowerCase()
            .includes(term),
      )
      .sort((a, b) => {
        const bd = new Date(b.appliedAt ?? b.createdAt).getTime();
        const ad = new Date(a.appliedAt ?? a.createdAt).getTime();
        return bd - ad;
      });
  }, [actions, statusFilter, locationFilter, search]);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-shrink-0 flex flex-wrap items-center gap-2 p-3 md:p-4 border-b border-gray-100 bg-white">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar en el histórico..."
            className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as "all" | "applied" | "discarded")
          }
          className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="all">Todos los estados</option>
          <option value="applied">Aplicadas</option>
          <option value="discarded">Descartadas</option>
        </select>
        <select
          value={locationFilter}
          onChange={(e) => setLocationFilter(e.target.value)}
          className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent max-w-[220px]"
        >
          <option value="all">Todos los negocios</option>
          {businesses.map((b) => (
            <option key={b.locationId} value={b.locationId}>
              {b.locationName}
            </option>
          ))}
        </select>
        <button
          onClick={onReload}
          disabled={loading}
          title="Actualizar"
          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 md:p-4">
        {loading && !actions ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : error ? (
          <p className="text-sm text-red-600 text-center py-8">{error}</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-10">
            {(actions ?? []).filter((a) => a.status !== "pending").length ===
            0
              ? "Sin histórico todavía."
              : "Ningún cambio coincide con estos filtros."}
          </p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {filtered.map((a) => (
              <ActionCard
                key={a.id}
                action={a}
                busy={busyActionId === a.id}
                highlighted={highlightActionId === a.id}
                onApply={onApply}
                onMarkSent={onMarkSent}
                onCopyOrder={onCopyOrder}
                onDiscard={onDiscard}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pestaña "Conversaciones": lista con título, negocio, nº mensajes y fecha
// ---------------------------------------------------------------------------

function ConversationRow({
  conversation,
  active,
  busy,
  onSelect,
  onRename,
  onDelete,
}: {
  conversation: ConversationSummary;
  active: boolean;
  busy: boolean;
  onSelect: () => void;
  onRename: (title: string) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(conversation.title);

  useEffect(() => {
    setTitle(conversation.title);
  }, [conversation.title]);

  const save = () => {
    const trimmed = title.trim();
    if (trimmed && trimmed !== conversation.title) onRename(trimmed);
    setEditing(false);
  };
  const cancel = () => {
    setTitle(conversation.title);
    setEditing(false);
  };

  return (
    <div
      className={`rounded-xl border p-3 flex items-center gap-2 transition-colors ${
        active
          ? "border-primary-300 bg-primary-50/60"
          : "border-gray-200 bg-white hover:bg-gray-50"
      }`}
    >
      {editing ? (
        <input
          type="text"
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              save();
            }
            if (e.key === "Escape") cancel();
          }}
          maxLength={120}
          className="flex-1 min-w-0 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      ) : (
        <button
          type="button"
          onClick={onSelect}
          className="flex-1 min-w-0 text-left"
        >
          <p className="font-medium text-gray-900 text-sm truncate">
            {conversation.title || "Conversación sin título"}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {conversation.locationName ?? "Todos los negocios"} ·{" "}
            {conversation.messagesCount}{" "}
            {conversation.messagesCount === 1 ? "mensaje" : "mensajes"} ·{" "}
            {formatDateTime(conversation.lastMessageAt)}
          </p>
        </button>
      )}

      {editing ? (
        <div className="flex-shrink-0 flex items-center gap-1">
          <button
            type="button"
            onClick={save}
            title="Guardar"
            className="p-1.5 rounded hover:bg-gray-100 text-emerald-600"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={cancel}
            title="Cancelar"
            className="p-1.5 rounded hover:bg-gray-100 text-gray-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="flex-shrink-0 flex items-center gap-1">
          <button
            type="button"
            onClick={() => setEditing(true)}
            disabled={busy}
            title="Renombrar"
            className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={busy}
            title="Borrar"
            className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-red-600 disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
          </button>
        </div>
      )}
    </div>
  );
}

function ConversationsTab({
  conversations,
  loading,
  error,
  onReload,
  activeConversationId,
  busyConversationId,
  onSelect,
  onNew,
  onRename,
  onDelete,
}: {
  conversations: ConversationSummary[] | null;
  loading: boolean;
  error: string | null;
  onReload: () => void;
  activeConversationId: string | null;
  busyConversationId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string, title: string) => void;
}) {
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-shrink-0 flex items-center justify-between gap-2 p-3 md:p-4 border-b border-gray-100 bg-white">
        <p className="text-sm text-gray-500">
          {conversations ? `${conversations.length} conversación(es)` : ""}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onNew}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-primary-600 text-white text-xs font-medium hover:bg-primary-700"
          >
            <Plus className="w-3.5 h-3.5" /> Nueva conversación
          </button>
          <button
            onClick={onReload}
            disabled={loading}
            title="Actualizar"
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
          >
            <RefreshCw
              className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-2">
        {loading && !conversations ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : error ? (
          <p className="text-sm text-red-600 text-center py-8">{error}</p>
        ) : (conversations ?? []).length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-10">
            Todavía no hay conversaciones guardadas.
          </p>
        ) : (
          (conversations ?? []).map((c) => (
            <ConversationRow
              key={c.id}
              conversation={c}
              active={c.id === activeConversationId}
              busy={busyConversationId === c.id}
              onSelect={() => onSelect(c.id)}
              onRename={(title) => onRename(c.id, title)}
              onDelete={() => onDelete(c.id, c.title)}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------

export default function CarmenBrain() {
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();

  const [mainTab, setMainTab] = useState<MainTab>("chat");

  // Negocios (reutiliza el listado de "Probar a Carmen")
  const [businesses, setBusinesses] = useState<BusinessItem[]>([]);
  const [businessesState, setBusinessesState] = useState<
    "loading" | "ready" | "error" | "disabled"
  >("loading");

  const loadBusinesses = useCallback(async () => {
    setBusinessesState("loading");
    try {
      const { data } = await apiClient.get<{
        success: boolean;
        data: BusinessItem[];
      }>("/admin/carmen-sim/businesses");
      setBusinesses(data.data);
      setBusinessesState("ready");
    } catch (err) {
      setBusinessesState(
        errorCode(err) === "CARMEN_SIM_DISABLED" ? "disabled" : "error",
      );
    }
  }, []);

  useEffect(() => {
    void loadBusinesses();
  }, [loadBusinesses]);

  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    null,
  );
  const selectedBusiness = useMemo(
    () => businesses.find((b) => b.locationId === selectedLocationId) ?? null,
    [businesses, selectedLocationId],
  );

  // Chat / conversación activa
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationTitle, setConversationTitle] = useState<string | null>(
    null,
  );
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [costEur, setCostEur] = useState(0);
  const [restoring, setRestoring] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [turns.length, sending]);

  // Acciones (pendientes + histórico, cargadas juntas y filtradas en cliente)
  const [actions, setActions] = useState<BrainAction[] | null>(null);
  const [actionsLoading, setActionsLoading] = useState(true);
  const [actionsError, setActionsError] = useState<string | null>(null);
  const [busyActionId, setBusyActionId] = useState<string | null>(null);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);
  const [highlightActionId, setHighlightActionId] = useState<string | null>(
    null,
  );

  const loadActions = useCallback(async () => {
    setActionsLoading(true);
    setActionsError(null);
    try {
      const { data } = await apiClient.get<{
        success: boolean;
        data: BrainAction[];
      }>("/admin/carmen-brain/actions", { params: { status: "all" } });
      setActions(data.data);
    } catch (err) {
      setActionsError(
        extractApiErrorMessage(err, "No se pudieron cargar las acciones"),
      );
    } finally {
      setActionsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadActions();
  }, [loadActions]);

  const pendingCount = useMemo(
    () => (actions ?? []).filter((a) => a.status === "pending").length,
    [actions],
  );

  const mergeActions = useCallback((incoming: BrainAction[]) => {
    if (incoming.length === 0) return;
    setActions((prev) => {
      const map = new Map((prev ?? []).map((a) => [a.id, a] as const));
      for (const a of incoming) map.set(a.id, a);
      return Array.from(map.values());
    });
  }, []);

  const applyActionUpdate = useCallback(
    (updated: BrainAction) => {
      mergeActions([updated]);
      setTurns((prev) =>
        prev.map((t) =>
          t.actions
            ? {
                ...t,
                actions: t.actions.map((a) =>
                  a.id === updated.id ? updated : a,
                ),
              }
            : t,
        ),
      );
    },
    [mergeActions],
  );

  // Conversaciones (pestaña "Conversaciones")
  const [conversations, setConversations] = useState<
    ConversationSummary[] | null
  >(null);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [conversationsError, setConversationsError] = useState<
    string | null
  >(null);
  const [busyConversationId, setBusyConversationId] = useState<string | null>(
    null,
  );

  const loadConversations = useCallback(async () => {
    setConversationsLoading(true);
    setConversationsError(null);
    try {
      const { data } = await apiClient.get<{
        success: boolean;
        data: ConversationSummary[];
      }>("/admin/carmen-brain/conversations");
      setConversations(data.data);
    } catch (err) {
      setConversationsError(
        extractApiErrorMessage(
          err,
          "No se pudieron cargar las conversaciones",
        ),
      );
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConversations();
  }, [loadConversations]);

  // Carga completa de una conversación (cambia turns/coste/negocio y la
  // recuerda como "última abierta"). Usada al restaurar, al hacer clic en
  // una fila de "Conversaciones" y al arrancar una precarga por caso.
  const loadConversationDetail = useCallback(
    async (id: string, opts?: { silent?: boolean }): Promise<boolean> => {
      try {
        const { data } = await apiClient.get<{
          success: boolean;
          data: {
            conversation: ConversationHeader;
            messages: ConversationMessage[];
          };
        }>(`/admin/carmen-brain/conversations/${id}`);
        const { conversation, messages } = data.data;
        setConversationId(conversation.id);
        setConversationTitle(conversation.title);
        setSelectedLocationId(conversation.locationId);
        setTurns(messages.map(toChatTurn));
        setCostEur(0);
        writeLastConversationId(conversation.id);
        mergeActions(messages.flatMap((m) => m.actions ?? []));
        return true;
      } catch (err) {
        if (!opts?.silent) {
          showToast(
            extractApiErrorMessage(err, "No se pudo cargar la conversación"),
            "error",
          );
        }
        return false;
      }
    },
    [mergeActions, showToast],
  );

  // Refresco ligero de los mensajes de la conversación ACTIVA tras
  // aplicar/descartar una acción: solo actualiza `turns` (para que aparezca
  // la nota de sistema y el estado nuevo de la tarjeta), sin tocar el coste
  // acumulado de la sesión ni el negocio seleccionado.
  const refreshActiveConversationMessages = useCallback(async (id: string) => {
    try {
      const { data } = await apiClient.get<{
        success: boolean;
        data: {
          conversation: ConversationHeader;
          messages: ConversationMessage[];
        };
      }>(`/admin/carmen-brain/conversations/${id}`);
      setTurns(data.data.messages.map(toChatTurn));
    } catch {
      // Refresco de cortesía, no una acción explícita: si falla, la
      // tarjeta ya se actualizó igualmente vía applyActionUpdate.
    }
  }, []);

  // ── Deep-links: ?action=<id> resalta una acción; ?case=<id>&locationId=
  // arranca una conversación NUEVA con el primer mensaje precargado. Sin
  // `?case=`, se intenta reabrir la última conversación desde localStorage.
  // Se consumen UNA vez al montar. ──
  useEffect(() => {
    const actionParam = searchParams.get("action");
    const caseParam = searchParams.get("case");
    const locationParam = searchParams.get("locationId");

    if (locationParam) setSelectedLocationId(locationParam);
    if (actionParam) setHighlightActionId(actionParam);

    const init = async () => {
      if (caseParam) {
        setConversationId(null);
        setConversationTitle(null);
        setTurns([]);
        setCostEur(0);
        writeLastConversationId(null);
        try {
          const { data } = await apiClient.get<{
            success: boolean;
            data: { case: { name: string } };
          }>(`/admin/carmen-sim/cases/${caseParam}`);
          setInput(
            `Revisa el caso guardado «${data.data.case.name}» y dime qué falló y qué cambiarías`,
          );
        } catch {
          showToast("No se pudo cargar el caso indicado", "error");
        }
      } else {
        const lastId = readLastConversationId();
        if (lastId) {
          const ok = await loadConversationDetail(lastId, { silent: true });
          if (!ok) writeLastConversationId(null);
        }
      }
      setRestoring(false);
    };
    void init();

    if (actionParam || caseParam || locationParam) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resalta y desplaza hasta la acción indicada por `?action=` una vez
  // cargado el listado, eligiendo dónde vive: pendiente → drawer/panel
  // lateral; resuelta → pestaña Histórico.
  useEffect(() => {
    if (!highlightActionId || !actions) return;
    const found = actions.find((a) => a.id === highlightActionId);
    if (!found) return;
    if (found.status === "pending") {
      setMobilePanelOpen(true);
    } else {
      setMainTab("historico");
    }
    const timer = setTimeout(() => {
      document
        .getElementById(`action-${highlightActionId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    const clearTimer = setTimeout(() => setHighlightActionId(null), 4000);
    return () => {
      clearTimeout(timer);
      clearTimeout(clearTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, highlightActionId]);

  const suggestions = useMemo(() => {
    const name = selectedBusiness?.locationName;
    return [
      name
        ? `¿Qué ha fallado esta semana en ${name}?`
        : "¿Qué ha fallado esta semana en este negocio?",
      "Revisa los casos guardados abiertos",
      name
        ? `¿Hay contradicciones en el prompt de ${name}?`
        : "¿Hay contradicciones en el prompt de este negocio?",
    ];
  }, [selectedBusiness]);

  const sendMessage = useCallback(
    async (rawText?: string) => {
      const text = (rawText ?? input).trim();
      if (!text || sending) return;
      const userTurn: ChatTurn = { role: "user", text, time: nowLabel() };
      setTurns((prev) => [...prev, userTurn]);
      setInput("");
      setSending(true);
      try {
        const { data } = await apiClient.post<{
          success: boolean;
          data: ChatResponseData;
        }>("/admin/carmen-brain/chat", {
          conversationId,
          locationId: selectedLocationId,
          message: text,
        });
        const resp = data.data;
        setTurns((prev) => [
          ...prev,
          {
            role: "assistant",
            text: resp.reply,
            toolCalls: resp.toolCalls,
            actions: resp.actions,
            time: nowLabel(),
          },
        ]);
        mergeActions(resp.actions);
        setCostEur((c) => c + (resp.usage?.costEur ?? 0));
        if (resp.conversationId && resp.conversationId !== conversationId) {
          setConversationId(resp.conversationId);
          writeLastConversationId(resp.conversationId);
        }
        void loadConversations();
      } catch (err) {
        const disabled = errorCode(err) === "CARMEN_BRAIN_DISABLED";
        setTurns((prev) => [
          ...prev,
          {
            role: "assistant",
            text: disabled
              ? "El cerebro de Carmen está apagado ahora mismo. Vuelve a intentarlo más tarde."
              : extractApiErrorMessage(
                  err,
                  "No se pudo responder. Inténtalo de nuevo.",
                ),
            time: nowLabel(),
            variant: disabled ? "info" : "error",
          },
        ]);
        if (disabled) {
          showToast("El cerebro de Carmen está apagado ahora mismo.", "info");
        }
      } finally {
        setSending(false);
      }
    },
    [
      input,
      sending,
      conversationId,
      selectedLocationId,
      mergeActions,
      showToast,
      loadConversations,
    ],
  );

  const handleComposerSubmit = (e: FormEvent) => {
    e.preventDefault();
    void sendMessage();
  };

  const handleNewConversation = useCallback(() => {
    setConversationId(null);
    setConversationTitle(null);
    setTurns([]);
    setCostEur(0);
    setInput("");
    writeLastConversationId(null);
    setMainTab("chat");
  }, []);

  const handleSelectConversation = useCallback(
    async (id: string) => {
      if (id === conversationId) {
        setMainTab("chat");
        return;
      }
      setBusyConversationId(id);
      const ok = await loadConversationDetail(id);
      setBusyConversationId(null);
      if (ok) setMainTab("chat");
    },
    [conversationId, loadConversationDetail],
  );

  const handleRenameConversation = useCallback(
    async (id: string, title: string) => {
      setBusyConversationId(id);
      try {
        await apiClient.patch(`/admin/carmen-brain/conversations/${id}`, {
          title,
        });
        setConversations(
          (prev) =>
            prev?.map((c) => (c.id === id ? { ...c, title } : c)) ?? prev,
        );
        if (id === conversationId) setConversationTitle(title);
        showToast("Conversación renombrada", "success");
      } catch (err) {
        showToast(
          extractApiErrorMessage(err, "No se pudo renombrar la conversación"),
          "error",
        );
      } finally {
        setBusyConversationId(null);
      }
    },
    [conversationId, showToast],
  );

  const handleDeleteConversation = useCallback(
    async (id: string, title: string) => {
      const ok = await confirm({
        title: "¿Borrar esta conversación?",
        message: `Se borrará «${title || "esta conversación"}» y no se puede deshacer.`,
        confirmText: "Sí, borrar",
        danger: true,
      });
      if (!ok) return;
      setBusyConversationId(id);
      try {
        await apiClient.delete(`/admin/carmen-brain/conversations/${id}`);
        setConversations((prev) => prev?.filter((c) => c.id !== id) ?? prev);
        showToast("Conversación borrada", "success");
        if (id === conversationId) handleNewConversation();
      } catch (err) {
        showToast(
          extractApiErrorMessage(err, "No se pudo borrar la conversación"),
          "error",
        );
      } finally {
        setBusyConversationId(null);
      }
    },
    [conversationId, confirm, showToast, handleNewConversation],
  );

  const handleApply = useCallback(
    async (action: BrainAction) => {
      const ok = await confirm({
        title: "¿Aplicar este cambio?",
        message: `Vas a cambiar las instrucciones de ${action.locationName ?? "este negocio"}. ¿Aplicar?`,
        confirmText: "Sí, aplicar",
      });
      if (!ok) return;
      setBusyActionId(action.id);
      try {
        const { data } = await apiClient.post<{
          success: boolean;
          data: ActionMutationResult;
        }>(`/admin/carmen-brain/actions/${action.id}/apply`);
        applyActionUpdate(data.data);
        showToast("Cambio aplicado", "success");
        void loadActions();
        void loadConversations();
        if (data.data.conversationId && data.data.conversationId === conversationId) {
          void refreshActiveConversationMessages(data.data.conversationId);
        }
      } catch (err) {
        showToast(
          extractApiErrorMessage(err, "No se pudo aplicar el cambio"),
          "error",
        );
      } finally {
        setBusyActionId(null);
      }
    },
    [
      confirm,
      applyActionUpdate,
      showToast,
      loadActions,
      loadConversations,
      conversationId,
      refreshActiveConversationMessages,
    ],
  );

  const handleMarkSent = useCallback(
    async (action: BrainAction) => {
      setBusyActionId(action.id);
      try {
        const { data } = await apiClient.post<{
          success: boolean;
          data: ActionMutationResult;
        }>(`/admin/carmen-brain/actions/${action.id}/apply`);
        applyActionUpdate(data.data);
        showToast("Marcada como enviada", "success");
        void loadActions();
        void loadConversations();
        if (data.data.conversationId && data.data.conversationId === conversationId) {
          void refreshActiveConversationMessages(data.data.conversationId);
        }
      } catch (err) {
        showToast(
          extractApiErrorMessage(err, "No se pudo marcar como enviada"),
          "error",
        );
      } finally {
        setBusyActionId(null);
      }
    },
    [
      applyActionUpdate,
      showToast,
      loadActions,
      loadConversations,
      conversationId,
      refreshActiveConversationMessages,
    ],
  );

  const handleCopyOrder = useCallback(
    (action: BrainAction) => {
      navigator.clipboard
        .writeText(action.after)
        .then(() => showToast("Orden copiada al portapapeles", "success"))
        .catch(() =>
          showToast("No se pudo copiar. Selecciona el texto a mano.", "error"),
        );
    },
    [showToast],
  );

  const handleDiscard = useCallback(
    async (action: BrainAction, reason: string) => {
      setBusyActionId(action.id);
      try {
        const { data } = await apiClient.post<{
          success: boolean;
          data: ActionMutationResult;
        }>(`/admin/carmen-brain/actions/${action.id}/discard`, { reason });
        applyActionUpdate(data.data);
        showToast("Acción descartada", "success");
        void loadActions();
        void loadConversations();
        if (data.data.conversationId && data.data.conversationId === conversationId) {
          void refreshActiveConversationMessages(data.data.conversationId);
        }
      } catch (err) {
        showToast(
          extractApiErrorMessage(err, "No se pudo descartar"),
          "error",
        );
      } finally {
        setBusyActionId(null);
      }
    },
    [
      applyActionUpdate,
      showToast,
      loadActions,
      loadConversations,
      conversationId,
      refreshActiveConversationMessages,
    ],
  );

  const pendingPanelProps = {
    actions,
    loading: actionsLoading,
    error: actionsError,
    onReload: () => void loadActions(),
    busyActionId,
    highlightActionId,
    onApply: (a: BrainAction) => void handleApply(a),
    onMarkSent: (a: BrainAction) => void handleMarkSent(a),
    onCopyOrder: handleCopyOrder,
    onDiscard: (a: BrainAction, reason: string) => void handleDiscard(a, reason),
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-gray-50 overflow-hidden -m-4 lg:-m-8">
      {/* Cabecera */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-3 md:px-4 py-3 space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h1 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary-600" /> Habla con Carmen
          </h1>
          <div className="flex items-center gap-2">
            <button
              onClick={handleNewConversation}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            >
              <Plus className="w-3.5 h-3.5" /> Nueva conversación
            </button>
            <button
              onClick={() => setMobilePanelOpen(true)}
              className="lg:hidden relative inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-primary-50 text-primary-700 hover:bg-primary-100"
            >
              <Inbox className="w-3.5 h-3.5" /> Pendientes
              {pendingCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-primary-600 text-white text-[10px] font-semibold">
                  {pendingCount}
                </span>
              )}
            </button>
          </div>
        </div>
        <div>
          <select
            value={selectedLocationId ?? ""}
            onChange={(e) => setSelectedLocationId(e.target.value || null)}
            disabled={businessesState === "loading"}
            className="w-full sm:w-96 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50"
          >
            <option value="">Todos los negocios</option>
            {businesses.map((b) => (
              <option key={b.locationId} value={b.locationId}>
                {b.locationName} — {b.organizationName}
              </option>
            ))}
          </select>
          {businessesState === "disabled" && (
            <p className="text-xs text-blue-700 flex items-center gap-1 mt-1">
              <Info className="w-3.5 h-3.5 flex-shrink-0" /> El listado de
              negocios está apagado ahora mismo; puedes seguir preguntando
              sobre "Todos los negocios".
            </p>
          )}
          {businessesState === "error" && (
            <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> No se
              pudo cargar la lista de negocios.{" "}
              <button
                onClick={() => void loadBusinesses()}
                className="underline"
              >
                Reintentar
              </button>
            </p>
          )}
        </div>
      </div>

      {/* Barra de pestañas del área principal (Chat / Histórico /
          Conversaciones) — desplazable en móvil */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-3 md:px-4 py-2 overflow-x-auto">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-max">
          <button
            onClick={() => setMainTab("chat")}
            className={mainTabClasses(mainTab === "chat")}
          >
            <MessageSquare className="w-3.5 h-3.5" /> Chat
          </button>
          <button
            onClick={() => setMainTab("historico")}
            className={mainTabClasses(mainTab === "historico")}
          >
            <History className="w-3.5 h-3.5" /> Histórico
          </button>
          <button
            onClick={() => setMainTab("conversaciones")}
            className={mainTabClasses(mainTab === "conversaciones")}
          >
            <MessagesSquare className="w-3.5 h-3.5" /> Conversaciones
            {conversations && conversations.length > 0
              ? ` (${conversations.length})`
              : ""}
          </button>
        </div>
      </div>

      {/* Cuerpo: área principal (según pestaña) + panel de pendientes */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        <div className="flex-1 flex flex-col min-w-0">
          {mainTab === "chat" && (
            <>
              {conversationId && (
                <div className="flex-shrink-0 flex items-center justify-between gap-2 px-3 md:px-4 py-1.5 border-b border-gray-100 bg-gray-50/70 text-xs text-gray-500">
                  <span className="truncate">
                    Conversación:{" "}
                    <span className="font-medium text-gray-700">
                      {conversationTitle || "sin título"}
                    </span>
                  </span>
                  {costEur > 0 && (
                    <span className="flex-shrink-0">
                      Coste: {costEur.toFixed(4)} €
                    </span>
                  )}
                </div>
              )}
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-3 md:p-4"
              >
                {restoring ? (
                  <div className="h-full flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-400" />
                  </div>
                ) : turns.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-center p-6">
                    <div className="max-w-sm">
                      <Brain className="w-10 h-10 text-primary-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">
                        Pregunta por lo que ha pasado en un negocio, revisa
                        casos guardados o pide cambios en el prompt de Carmen.
                        Cada cambio se queda pendiente hasta que lo apliques.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {turns.map((turn, i) =>
                      turn.role === "system" ? (
                        <div key={i} className="flex justify-center">
                          <p className="max-w-[90%] text-center text-[11px] text-gray-400 bg-gray-100 px-3 py-1 rounded-full inline-flex items-center gap-1.5">
                            <Info className="w-3 h-3 flex-shrink-0" />
                            <span>{turn.text}</span>
                          </p>
                        </div>
                      ) : (
                        <BrainChatBubble
                          key={i}
                          turn={turn}
                          busyActionId={busyActionId}
                          highlightActionId={highlightActionId}
                          onApply={(a) => void handleApply(a)}
                          onMarkSent={(a) => void handleMarkSent(a)}
                          onCopyOrder={handleCopyOrder}
                          onDiscard={(a, reason) =>
                            void handleDiscard(a, reason)
                          }
                        />
                      ),
                    )}
                    {sending && (
                      <div className="flex justify-start">
                        <div className="px-3.5 py-2.5 rounded-2xl bg-white border border-gray-200 text-xs text-gray-500 rounded-tl-md">
                          Carmen está pensando…
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {!restoring && turns.length === 0 && (
                <div className="flex-shrink-0 px-3 md:px-4 pt-2 flex flex-wrap gap-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => void sendMessage(s)}
                      className="text-xs px-3 py-1.5 rounded-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              <ConversationComposer
                value={input}
                onChange={setInput}
                onSubmit={handleComposerSubmit}
                sending={sending}
                placeholder="Pregunta o pide un cambio... (Intro para enviar)"
              />
            </>
          )}

          {mainTab === "historico" && (
            <HistoryTab
              actions={actions}
              loading={actionsLoading}
              error={actionsError}
              onReload={() => void loadActions()}
              businesses={businesses}
              busyActionId={busyActionId}
              highlightActionId={highlightActionId}
              onApply={(a) => void handleApply(a)}
              onMarkSent={(a) => void handleMarkSent(a)}
              onCopyOrder={handleCopyOrder}
              onDiscard={(a, reason) => void handleDiscard(a, reason)}
            />
          )}

          {mainTab === "conversaciones" && (
            <ConversationsTab
              conversations={conversations}
              loading={conversationsLoading}
              error={conversationsError}
              onReload={() => void loadConversations()}
              activeConversationId={conversationId}
              busyConversationId={busyConversationId}
              onSelect={(id) => void handleSelectConversation(id)}
              onNew={handleNewConversation}
              onRename={(id, title) => void handleRenameConversation(id, title)}
              onDelete={(id, title) => void handleDeleteConversation(id, title)}
            />
          )}
        </div>

        {/* Panel de escritorio */}
        <aside className="hidden lg:flex lg:flex-col w-80 flex-shrink-0 border-l border-gray-200 bg-white overflow-hidden">
          <PendingActionsPanel {...pendingPanelProps} />
        </aside>
      </div>

      {/* Panel móvil (drawer) */}
      {mobilePanelOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setMobilePanelOpen(false)}
          />
          <div className="fixed inset-y-0 right-0 z-50 w-80 max-w-[85vw] bg-white border-l border-gray-200 lg:hidden flex flex-col">
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900 text-sm">
                Cambios propuestos
              </h2>
              <button
                onClick={() => setMobilePanelOpen(false)}
                className="p-1 rounded hover:bg-gray-100 text-gray-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <PendingActionsPanel {...pendingPanelProps} />
          </div>
        </>
      )}
    </div>
  );
}
